<template>
  <page-container :title="title">
    <v-row class="ma-0 mb-2 pa-2 tab-header" align-content="center">
      <v-tabs height="36">
        <v-tab>Location details</v-tab>
        <v-spacer></v-spacer>
        <feature name="warehouse" variant="write" data="locations">
          <v-btn
            tile
            v-show="locationInfoChanged"
            class="button background-color--inactive color--foreground"
            @click="cancelChanges"
            :disabled="saving"
            >Cancel</v-btn
          >
          <v-btn
            tile
            color="primary"
            class="mx-2 button"
            v-show="locationInfoChanged"
            :disabled="!locationInfoChanged || saving"
            @click="saveEditedLocation"
            :loading="saving"
            >Save</v-btn
          >
          <v-btn icon @click="deleteLocation" :disabled="saving"
            ><v-icon>mdi-delete</v-icon></v-btn
          >
        </feature>
      </v-tabs>
    </v-row>
    <v-row class="ma-0">
      <v-sheet style="width: 100%">
        <v-container>
          <v-form v-model="formValid">
            <v-row align="center" class="ma-0">
              <v-col cols="2" class="details-header">Name</v-col>
              <v-col cols="10" class="py-2">
                <v-text-field
                  solo
                  flat
                  dense
                  v-model="editedLocationInfo.name"
                  class="details-field px-2"
                  background-color="transparent"
                  :hide-details="
                    rules.required(editedLocationInfo.name) === true
                  "
                  :rules="[rules.required]"
                  required
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row align="center" class="ma-0">
              <v-col cols="2" class="details-header">Address line 1</v-col>
              <v-col cols="10" class="py-2">
                <v-text-field
                  solo
                  flat
                  dense
                  v-model="editedLocationInfo.addressLine1"
                  class="details-field px-2"
                  background-color="transparent"
                  :hide-details="true"
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row align="center" class="ma-0">
              <v-col cols="2" class="details-header">Address line 2</v-col>
              <v-col cols="10" class="py-2">
                <v-text-field
                  solo
                  flat
                  dense
                  v-model="editedLocationInfo.addressLine2"
                  class="details-field px-2"
                  background-color="transparent"
                  :hide-details="true"
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row align="center" class="ma-0">
              <v-col cols="2" class="details-header">Town / City</v-col>
              <v-col cols="10" class="py-2">
                <v-text-field
                  solo
                  flat
                  dense
                  v-model="editedLocationInfo.city"
                  class="details-field px-2"
                  background-color="transparent"
                  :hide-details="true"
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row align="center" class="ma-0">
              <v-col cols="2" class="details-header">Postcode</v-col>
              <v-col cols="10" class="py-2">
                <v-text-field
                  solo
                  flat
                  dense
                  v-model="editedLocationInfo.postcode"
                  class="details-field px-2"
                  background-color="transparent"
                  :hide-details="true"
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row align="center" class="ma-0">
              <v-col cols="2" class="details-header">Country</v-col>
              <v-col cols="10" class="py-2">
                <v-select
                  solo
                  flat
                  dense
                  :items="countries"
                  item-text="name"
                  item-value="name"
                  v-model="editedLocationInfo.country"
                  class="details-field px-2"
                  background-color="transparent"
                  return-object
                  :hide-details="true"
                  menu-props="auto"
                ></v-select>
              </v-col>
            </v-row>
          </v-form>
        </v-container>
      </v-sheet>
    </v-row>
    <v-row class="ma-0 mt-2">
      <v-sheet style="width: 100%">
        <location-areas-list :locationId="id"></location-areas-list>
      </v-sheet>
    </v-row>
  </page-container>
</template>

<script>
import PageContainer from '@/components/PageContainer.vue'
import { mapGetters, mapActions } from 'vuex'
import { db } from '@/firebaseConfig'
import { textFieldRules } from '@/common'
import countryList from 'country-list'
import LocationAreasList from './components/LocationAreasList.vue'
import { objects } from '@/helpers'

export default {
  name: 'LocationDetailsView',
  props: {
    id: {
      type: String,
      required: true,
    },
  },
  components: {
    PageContainer,
    LocationAreasList,
  },
  data: () => ({
    loading: false,
    saving: false,
    formValid: false,
    countries: countryList.getData(),
    rules: textFieldRules,
    title: [
      { to: 'warehouse-locations', text: 'Locations' },
      {
        text: 'View location',
      },
    ],
    locationInfo: {},
    editedLocationInfo: { country: {} },
    unsubscribe: null,
  }),
  computed: {
    ...mapGetters(['companyId']),
    dataBasePath() {
      return `companies/${this.companyId}/locations/${this.id}`
    },
    locationInfoChanged() {
      return (
        JSON.stringify(this.locationInfo) !==
        JSON.stringify(this.editedLocationInfo)
      )
    },
    docRef() {
      return db.collection(`companies/${this.companyId}/locations`).doc(this.id)
    },
  },
  created() {
    this.getInfo()
  },
  beforeDestroy() {
    if (this.unsubscribe) {
      this.unsubscribe()
    }
  },
  methods: {
    ...mapActions(['showError', 'showSuccess', 'showDeletionSuccess']),
    async getInfo() {
      this.loading = true
      if (this.unsubscribe) {
        this.unsubscribe()
      }

      this.unsubscribe = this.docRef.onSnapshot(
        (doc) => {
          this.locationInfo = doc.data()
          this.editedLocationInfo = Object.assign({}, this.locationInfo)
          this.loading = false
        },
        (error) => {
          console.error(error)
          this.loading = false
        }
      )
    },
    cancelChanges() {
      this.editedLocationInfo = Object.assign(
        { country: {} },
        this.locationInfo
      )
    },
    async saveEditedLocation() {
      this.saving = true
      try {
        await this.docRef.update(
          objects.withUpdatedDataInfo(this.editedLocationInfo)
        )
        const name = this.editedLocationInfo.name
        this.$router.replace({ name: 'warehouse-locations' })

        this.$nextTick(() =>
          this.showSuccess(
            `Location <b>${name}</b> was successsfully <b>updated</b>`
          )
        )
      } catch (error) {
        console.error(error)
        this.showError('<b>Failed to save.</b> Please try again later')
      } finally {
        this.saving = false
      }
    },
    async deleteLocation() {
      const { name } = this.editedLocationInfo
      const result = await this.$confirm(
        'This will permanently remove the location from your warehouse and from any associated materials.',
        { title: 'Delete location' }
      )

      if (result === true) {
        try {
          await this.docRef.delete()
          this.$router.replace({ name: 'warehouse-locations' })
          this.$nextTick(() =>
            this.showDeletionSuccess(
              `Location <b>${name}</b> has been successfully <b>deleted</b>`
            )
          )
        } catch (error) {
          console.error(error)
          this.showError('<b>Failed.</b> Please try again later')
        }
      }
    },
  },
}
</script>

<style lang="sass" scoped>
.details-header
  text-align: right
  @include body_dark
</style>

<style lang="sass">
.details-field
  background-color: $background !important
</style>
